import { getWidgets } from '@nykaa/app/store/service';
import userInfoByAttribute from '@nykaa/auth-shared/store/actions/userInfo';
import { Device } from '@nykaa/data-layer/constants';
import { logger } from '@nykaa/logger';
import logErrors from '@nykaa/logger/logErrors';
import { PHPSESSID_COOKIE_KEY } from '@nykaa/server/constants';
import { setCookie } from '@nykaa/utils/cookies';
import { isValidObject, isValidString } from '@nykaa/utils/validations';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';
import { localStorageService } from 'client/service/storage';
import { reactStorageKeys } from 'constants/auth';
import { DN_PAGE_DATA, DN_PAGE_SECTION } from 'constants/beautyPortfolio';
import {
  AB_CONVENIENCE_FEE,
  MINI_COUPONS_OFFERS,
  AB_NYKAA_NOW_PDP,
  ERROR_BOUNDARY,
  AB_SHOW_ASPECTS,
  PLP_VARIANT_SELECTOR,
  AB_FREE_GIFT,
} from 'constants/experiments';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { isDeviceAndroid } from 'helpers/utils/device';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAuth,
  updateCartProductQuantities,
  updateWishlistIds,
} from 'shared/store/auth/action';
import { State } from 'store/types';
import { PRIVE_ERROR_MSG } from 'User/constants/loyalty';
import { UseGetAppTrackingDataParams } from '../layouts/ConversationalSearch/types';
import {
  isPlpVariantSelectorFashionEligible,
  isPlpVariantSelectorGroomingEligible,
} from 'helpers/utils/product';
import { PrimaryCategories } from 'store/product/types';
import { ATTRIBUTE_CUSTOMER_SEGMENT } from '../constants';
import { useIsUserSegmentEnabled } from './beautyPortfolio';

export const useDeviceId = (): Device => {
  const deviceId = useSelector(
    (state: State) => state.appReducer.client.device
  );
  return deviceId;
};

export const useFetchWidgets = ({
  pageSection = '',
  pageType = '',
  pageData = '',
}) => {
  const [hasError, setError] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const userSegmentExpEnabled = useIsUserSegmentEnabled();
  const [userSegment, setUserSegment] = useState({ fetched: false, value: {} });

  const fetchData = useCallback(async () => {
    try {
      const res = await getWidgets(
        {
          page_section: pageSection || DN_PAGE_SECTION,
          page_data: pageData || DN_PAGE_DATA,
          page_type: pageType || '',
        },
        __PLATFORM__
      );
      return res;
    } catch (error) {
      setError(true);
      logErrors(error, PRIVE_ERROR_MSG);
    } finally {
      setLoading(false);
    }
  }, [pageData, pageSection, pageType]);

  useEffect(() => {
    if (userSegmentExpEnabled && !userSegment.fetched) return;
    fetchData()
      .then((res) => setWidgets(res))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, userSegment.fetched]);

  useEffect(() => {
    if (!userSegmentExpEnabled) return;
    userInfoByAttribute(ATTRIBUTE_CUSTOMER_SEGMENT)
      .then((res) => {
        const { cust_premium_segment } = res || {};
        const { premiumness_bucket } = cust_premium_segment || {};
        setUserSegment({
          fetched: true,
          value: premiumness_bucket ? { premiumness_bucket } : {},
        });
      })
      .catch(() => {
        setUserSegment({ fetched: true, value: {} });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    hasError,
    isLoading,
    widgets,
    userSegment: userSegment?.value,
    userSegmentExpEnabled,
  };
};

export const useIsErrorBoundaryEnabled = () => {
  const isErrorBoundaryEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, ERROR_BOUNDARY, 'A')
  );
  return isErrorBoundaryEnabled;
};

export const useGetAppTrackingData = ({
  onTrackingDataUpdate,
  setCartCountValue,
  getDeepLinkTrackingData,
}: UseGetAppTrackingDataParams) => {
  const dispatch = useDispatch();

  const handleTrackingMetaData = (data: string | object) => {
    try {
      const parsedData = isValidString(data) ? JSON.parse(data) : data;
      if (parsedData && isValidObject(parsedData)) {
        onTrackingDataUpdate(parsedData);
      }
    } catch (error) {
      logErrors(error, 'Error in parsing tracking meta data from app');
    }
  };

  const updateSession = (sessionId: string) => {
    if (isValidString(sessionId)) {
      try {
        setCookie(PHPSESSID_COOKIE_KEY, sessionId, 100);
        localStorageService.setItem(reactStorageKeys.CONFIG_EXPIRY, '0');
      } catch (error) {
        logger.error('Error in setting session id from app', error);
      }
      fetchAuth()(dispatch)
        .then(() => {
          logger.info(
            'ReactConfig API called successfully after session update'
          );
        })
        .catch((error) => {
          logger.error('Error in setting session id from app', error);
        });
    }
  };

  const device = useSelector((state: State) => state.appReducer?.client.device);
  const isAndroid = isDeviceAndroid(device);
  const requestTrackingDataFromApp = () => {
    const webViewParams = {
      device,
      callbackFunctionName: webViewCallbacks.getTrackingMetaData,
      callbackParams: isAndroid ? null : {},
      passValidParamsOnly: true,
    };
    webViewCallbackhandler(webViewParams);
  };

  const updateUserSessionData = (
    wishlistIds: string[],
    productVsItemCount: Record<string, number>
  ) => {
    if (wishlistIds && Array.isArray(wishlistIds)) {
      dispatch(updateWishlistIds(wishlistIds));
    }
    if (productVsItemCount) {
      dispatch(updateCartProductQuantities(productVsItemCount));
      setCartCountValue(
        Object.values(productVsItemCount).reduce((a, b) => a + b, 0)
      );
    }
  };

  useEffect(() => {
    if (!window.NYKAA_WEB_INTERFACE) {
      window.NYKAA_WEB_INTERFACE = {};
    }
    window.NYKAA_WEB_INTERFACE.setTrackingMetaData = handleTrackingMetaData;
    window.NYKAA_WEB_INTERFACE.setCartCount = setCartCountValue;
    window.NYKAA_WEB_INTERFACE.updateSession = updateSession;
    window.NYKAA_WEB_INTERFACE.updateUserSessionData = updateUserSessionData;
    requestTrackingDataFromApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!window.NYKAA_WEB_INTERFACE) {
      window.NYKAA_WEB_INTERFACE = {};
    }
    window.NYKAA_WEB_INTERFACE.getDeepLinkTrackingData =
      getDeepLinkTrackingData;
  }, [getDeepLinkTrackingData]);

  return {};
};

export const useIsConvenienceFeeEnabled = () => {
  const isConvenienceFeeEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_CONVENIENCE_FEE, 'A')
    ) || false;
  return isConvenienceFeeEnabled;
};

export const useIsNykaaNowPdEnabled = () => {
  const isNykaaNowPdEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_NYKAA_NOW_PDP, 'A')
  );
  return isNykaaNowPdEnabled;
};

export const useSafeIdleCallback = (callback: () => void): void => {
  useEffect(() => {
    let idleCallbackId: number | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if ('requestIdleCallback' in window) {
      idleCallbackId = requestIdleCallback(callback);
    } else {
      timeoutId = setTimeout(callback, 200);
    }

    return () => {
      if (idleCallbackId !== null && 'cancelIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [callback]);
};

export const useIsFreeGiftOnPdpEnabled = () => {
  const isFreeGiftOnPdpEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_FREE_GIFT, 'A')
    ) || false;
  return isFreeGiftOnPdpEnabled;
};

export const useIsMiniCouponsAndOffersEnabled = () => {
  const isMiniCouponsAndOffersEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, MINI_COUPONS_OFFERS, 'A')
    ) || false;
  return isMiniCouponsAndOffersEnabled;
};

export const useIsAspectExpEnabled = () => {
  const isAspectExpEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_SHOW_ASPECTS, 'A')
    ) || false;
  return isAspectExpEnabled;
};

export const useIsPLPVariantExpEnabled = (
  productCategoriesFashion: string[],
  productCategoriesGrooming: string[],
  primaryCategories: PrimaryCategories
) => {
  const isPlpVariantExpEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, PLP_VARIANT_SELECTOR, 'A')
    ) || false;
  const isPlpVariantSelectorFashionEnabled =
    isPlpVariantSelectorFashionEligible(
      productCategoriesFashion,
      isPlpVariantExpEnabled,
      primaryCategories
    ) || false;
  const isPlpVariantSelectorGroomingEnabled =
    isPlpVariantSelectorGroomingEligible(
      productCategoriesGrooming,
      isPlpVariantExpEnabled,
      primaryCategories
    ) || false;

  return {
    isPlpVariantSelectorFashionEnabled,
    isPlpVariantSelectorGroomingEnabled,
  };
};
